// Auth + profile API client for web-pro.
//
// Access token: kept in localStorage (sent as Bearer). Refresh token: httpOnly
// cookie managed by the server (we just send credentials). On a 401 we try one
// silent /refresh, then retry the original request.
//
// The active profile is mirrored into localStorage `landhep_profile` as a cache
// so existing synchronous readers keep working; the DB is the source of truth.

const API = import.meta.env.VITE_API_URL || "";

const ACCESS_KEY  = "landhep_access_token";
const PROFILE_KEY = "landhep_profile";

export const getToken = () => localStorage.getItem(ACCESS_KEY) || "";
export const setToken = (t) => t ? localStorage.setItem(ACCESS_KEY, t) : localStorage.removeItem(ACCESS_KEY);

function cacheProfile(profile) {
  if (profile) localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

async function rawRefresh() {
  const res = await fetch(`${API}/api/auth/refresh`, { method: "POST", credentials: "include" });
  if (!res.ok) return false;
  const data = await res.json();
  setToken(data.access_token);
  return true;
}

// Authenticated fetch with one silent refresh-and-retry on 401.
async function authFetch(path, opts = {}, _retried = false) {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    credentials: "include",
    headers: {
      ...(opts.body ? { "Content-Type": "application/json" } : {}),
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
      ...(opts.headers || {}),
    },
  });
  if (res.status === 401 && !_retried) {
    if (await rawRefresh()) return authFetch(path, opts, true);
  }
  return res;
}

async function jsonOrThrow(res) {
  let data = null;
  try { data = await res.json(); } catch {}
  if (!res.ok) throw new Error(data?.detail || `Request failed (${res.status})`);
  return data;
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export async function register({ email, password, full_name }) {
  const data = await jsonOrThrow(await fetch(`${API}/api/auth/register`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, full_name }),
  }));
  setToken(data.access_token);
  return data.user;
}

export async function login({ email, password }) {
  const data = await jsonOrThrow(await fetch(`${API}/api/auth/login`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  }));
  setToken(data.access_token);
  return data.user;
}

export async function logout() {
  try { await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" }); } catch {}
  setToken("");
  localStorage.removeItem(PROFILE_KEY);
}

export async function me() {
  const data = await jsonOrThrow(await authFetch(`/api/auth/me`));
  return data.user;
}

// Restore a session on app load using the refresh cookie. Returns user|null.
export async function restoreSession() {
  if (!(await rawRefresh())) { setToken(""); return null; }
  try { return await me(); } catch { return null; }
}

// ── Profiles ──────────────────────────────────────────────────────────────────
export async function getActiveProfile() {
  const data = await jsonOrThrow(await authFetch(`/api/profiles/active`));
  if (data.profile) cacheProfile(data.profile);
  return data.profile;
}

export async function listProfiles() {
  const data = await jsonOrThrow(await authFetch(`/api/profiles`));
  return data.profiles;
}

export async function createProfile(profile) {
  const data = await jsonOrThrow(await authFetch(`/api/profiles`, {
    method: "POST", body: JSON.stringify(profile),
  }));
  cacheProfile(data.profile);
  return data.profile;
}

export async function updateProfile(id, profile) {
  const data = await jsonOrThrow(await authFetch(`/api/profiles/${id}`, {
    method: "PUT", body: JSON.stringify(profile),
  }));
  cacheProfile(data.profile);
  return data.profile;
}

export async function activateProfile(id) {
  await jsonOrThrow(await authFetch(`/api/profiles/${id}/activate`, { method: "POST" }));
}

// How many profiles (= businesses) the user has vs the admin-set cap.
export async function getProfileSlots() {
  return jsonOrThrow(await authFetch(`/api/profiles/slots`));
}

// ── In-app notifications ─────────────────────────────────────────────────────
export async function getNotifications() {
  return jsonOrThrow(await authFetch(`/api/notifications`));
}
export async function markNotificationsRead(ids) {
  return jsonOrThrow(await authFetch(`/api/notifications/read`, {
    method: "POST", body: JSON.stringify(ids ? { ids } : {}),
  }));
}

// Admin-configurable selection caps (POI priorities, anchors, compare bucket, ...).
export async function getProfileLimits(id) {
  const data = await jsonOrThrow(await authFetch(`/api/profiles/${id}/limits`));
  return data.limits;
}

// Global defaults, for use before a profile exists (e.g. onboarding).
export async function getProfileLimitDefaults() {
  const data = await jsonOrThrow(await fetch(`${API}/api/profile-limit-defaults`));
  return data.limits;
}

// ── Credits & billing (user self-serve) ───────────────────────────────────────
export async function getCredits() {
  return jsonOrThrow(await authFetch(`/api/credits`));   // { balance, ledger[] }
}

// Fired after anything that changes the credit balance, so the navbar balance
// chip (and any other listener) can refresh instantly instead of waiting for a
// remount/navigation.
export function emitBalanceChanged() {
  try { window.dispatchEvent(new Event("landhep:balance")); } catch { /* SSR/no-window */ }
}

export async function searchCities(q = "", limit = 20) {
  const data = await jsonOrThrow(await authFetch(`/api/cities?q=${encodeURIComponent(q)}&limit=${limit}`));
  return data.cities;
}

export async function getOwnedCities(profileId) {
  const data = await jsonOrThrow(await authFetch(`/api/profiles/${profileId}/owned-cities`));
  return data.cities;
}

export async function openCity(profileId, kodeKabkot) {
  const data = await jsonOrThrow(await authFetch(`/api/profiles/${profileId}/cities/${kodeKabkot}/open`, { method: "POST" }));
  emitBalanceChanged();
  return data;
}

export async function renewCity(profileId, kodeKabkot) {
  const data = await jsonOrThrow(await authFetch(`/api/profiles/${profileId}/cities/${kodeKabkot}/renew`, { method: "POST" }));
  emitBalanceChanged();
  return data;
}

// ── Deep-analysis report versions ─────────────────────────────────────────────
export async function getZoneVersions(gridId) {
  const data = await jsonOrThrow(await authFetch(`/api/owned-zones/${gridId}/versions`));
  return data.versions;
}

// ── Bagikan laporan (tautan publik) ───────────────────────────────────────────
// Owner side (auth). { kind, gridId, storeId, title, ownerLabel, payload, password, expiresDays }
export async function createShare(body) {
  const d = await jsonOrThrow(await authFetch(`/api/share`, { method: "POST", body: JSON.stringify(body) }));
  return d.share;
}
export async function listShares({ kind, gridId, storeId } = {}) {
  const p = new URLSearchParams();
  if (kind) p.set("kind", kind);
  if (storeId) p.set("storeId", storeId); else if (gridId != null) p.set("gridId", String(gridId));
  const d = await jsonOrThrow(await authFetch(`/api/share?${p.toString()}`));
  return d.shares || [];
}
export async function revokeShare(id) {
  return jsonOrThrow(await authFetch(`/api/share/${id}`, { method: "DELETE" }));
}
// Public side (no login). The unlock cookie is scoped to the token's API path.
export async function getShare(token) {
  return jsonOrThrow(await fetch(`${API}/api/share/${encodeURIComponent(token)}`, { credentials: "include" }));
}
export async function unlockShare(token, password) {
  return jsonOrThrow(await fetch(`${API}/api/share/${encodeURIComponent(token)}/unlock`, {
    method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  }));
}

// ── Asisten AI ────────────────────────────────────────────────────────────────
// { configured, enabled, daily_cap, used_today, model }
export async function getAssistantStatus() {
  return jsonOrThrow(await authFetch(`/api/assistant/status`));
}

export async function listAssistantConversations() {
  const d = await jsonOrThrow(await authFetch(`/api/assistant/conversations`));
  return d.conversations || [];
}

// { id, title, messages: [{id, role, text, actions, meta, createdAt}] }
export async function getAssistantConversation(id) {
  return jsonOrThrow(await authFetch(`/api/assistant/conversations/${id}`));
}

export async function deleteAssistantConversation(id) {
  return jsonOrThrow(await authFetch(`/api/assistant/conversations/${id}`, { method: "DELETE" }));
}

export async function deleteAllAssistantConversations() {
  return jsonOrThrow(await authFetch(`/api/assistant/conversations`, { method: "DELETE" }));
}

// One turn. conversationId null → the server opens a new conversation and returns its id.
// Returns { configured, conversationId, id, text, actions, meta, usedToday, dailyCap }.
export async function postAssistantChat({ message, conversationId }) {
  return jsonOrThrow(await authFetch(`/api/assistant/chat`, {
    method: "POST", body: JSON.stringify({ message, conversationId }),
  }));
}

// Fire-and-forget product-activity beacon (analysis_open, deep_analysis_open, …).
export function logEvent(event) {
  try { authFetch(`/api/events`, { method: "POST", body: JSON.stringify(event) }); } catch { /* ignore */ }
}

export async function getBillingStatus() {
  const data = await jsonOrThrow(await fetch(`${API}/api/billing-status`));
  return data;   // { enabled }
}

export async function getCityPrice(kodeKabkot) {
  return jsonOrThrow(await authFetch(`/api/cities/${kodeKabkot}`));
}

export async function getZoneVersion(gridId, versionNo) {
  const data = await jsonOrThrow(await authFetch(`/api/owned-zones/${gridId}/versions/${versionNo}`));
  return data.version;
}

// ── Owned zones (deep analysis) ───────────────────────────────────────────────
export async function listOwnedZones() {
  const data = await jsonOrThrow(await authFetch(`/api/owned-zones`));
  return data.zones;
}

export async function saveOwnedZone(zone) {
  const data = await jsonOrThrow(await authFetch(`/api/owned-zones`, {
    method: "POST", body: JSON.stringify(zone),
  }));
  return data.zone;
}

export async function deleteOwnedZone(gridId) {
  await authFetch(`/api/owned-zones/${gridId}`, { method: "DELETE" });
}

export async function clearOwnedZones() {
  await authFetch(`/api/owned-zones`, { method: "DELETE" });
}

export async function getAICache(gridId) {
  const data = await jsonOrThrow(await authFetch(`/api/owned-zones/${gridId}/ai-cache`));
  return data.cache;
}

// Price a deep-analysis run WITHOUT charging — used to confirm cost before the
// billed run. reportType: 'klasik' | 'potensi_bisnis' | 'potensi_iklan'.
// Returns { ok, billing_enabled, is_first, price, ... }.
export async function getDeepAnalysisQuote(gridId, subcategory, radius = "3km", reportType = "klasik") {
  const p = new URLSearchParams({ grid_id: String(gridId), radius, report_type: reportType });
  if (subcategory) p.set("subcategory", subcategory);
  return jsonOrThrow(await authFetch(`/api/owned-zones/quote?${p.toString()}`));
}

// ── Skenario typed reports (Profil Lokasi + Potensi Bisnis/Iklan) ──────────────
// Free factual grid profile (demographics + multi-radius catchment + roads/POI).
export async function getGridFacts(lat, lng) {
  const p = new URLSearchParams({ lat: String(lat), lng: String(lng) });
  return jsonOrThrow(await authFetch(`/api/potensi/facts?${p.toString()}`));
}

// Owned typed reports for a grid → { reports: { potensi_bisnis, potensi_iklan } }.
export async function getGridReports(gridId) {
  return jsonOrThrow(await authFetch(`/api/potensi/grid/${gridId}`));
}

// Grids the active profile owns a Potensi report for, grouped by type (sidebar tabs).
export async function getOwnedPotensiGrids() {
  return jsonOrThrow(await authFetch(`/api/potensi/owned-grids`));
}

// Charge + generate + store one typed report. body: {report_type, grid_id, lat, lng,
// radius, inputs, region fields}. Returns { ok, report, amount_charged }.
export async function generatePotensi(body) {
  return jsonOrThrow(await authFetch(`/api/potensi/generate`, {
    method: "POST", body: JSON.stringify(body),
  }));
}

export async function saveAICache(gridId, cacheKey, payload, meta) {
  const data = await jsonOrThrow(await authFetch(`/api/owned-zones/${gridId}/ai-cache`, {
    method: "PUT", body: JSON.stringify({ cacheKey, payload, meta: meta || {} }),
  }));
  emitBalanceChanged();   // deep analysis charged
  return data;
}

export async function recordPdfExport(gridId, meta) {
  try {
    await authFetch(`/api/owned-zones/${gridId}/pdf-export`, {
      method: "POST", body: JSON.stringify({ meta: meta || {} }),
    });
  } catch { /* logging only — never block the download */ }
}

// ── Comparison bucket (cross-device) ─────────────────────────────────────────
export async function getComparisonBucket() {
  const data = await jsonOrThrow(await authFetch(`/api/comparisons`));
  return data.bucket || [];
}

export async function putComparisonBucket(bucket) {
  await authFetch(`/api/comparisons`, {
    method: "PUT", body: JSON.stringify({ bucket }),
  });
}

export async function clearComparisonBucket() {
  await authFetch(`/api/comparisons`, { method: "DELETE" });
}

// ── Branch Radar (store-monitoring dashboard) ────────────────────────────────
// ── Skenario asset portfolio ─────────────────────────────────────────────────
export async function skListAssets() {
  const data = await jsonOrThrow(await authFetch(`/api/skenario/assets`));
  return data.assets || [];
}
export async function skCreateAsset(asset) {
  const data = await jsonOrThrow(await authFetch(`/api/skenario/assets`, {
    method: "POST", body: JSON.stringify(asset),
  }));
  return data.asset;
}
export async function skBulkAssets(assets) {
  return jsonOrThrow(await authFetch(`/api/skenario/assets/bulk`, {
    method: "POST", body: JSON.stringify({ assets }),
  }));
}
export async function skDeleteAsset(id) {
  await authFetch(`/api/skenario/assets/${id}`, { method: "DELETE" });
}

export async function brListStores(kodeKabkot) {
  const q = kodeKabkot ? `?kode_kabkot=${encodeURIComponent(kodeKabkot)}` : "";
  const data = await jsonOrThrow(await authFetch(`/api/branch-radar/stores${q}`));
  return data.stores || [];
}

export async function brListCities() {
  const data = await jsonOrThrow(await authFetch(`/api/branch-radar/cities`));
  return data.cities || [];
}

export async function brCreateStore(store) {
  const data = await jsonOrThrow(await authFetch(`/api/branch-radar/stores`, {
    method: "POST", body: JSON.stringify(store),
  }));
  return data.store;
}

export async function brCreateStoresBulk(stores, opts = {}) {
  return jsonOrThrow(await authFetch(`/api/branch-radar/stores/bulk`, {
    method: "POST", body: JSON.stringify({ stores, ...opts }),
  }));
}

export async function brUpdateStore(id, patch) {
  const data = await jsonOrThrow(await authFetch(`/api/branch-radar/stores/${id}`, {
    method: "PATCH", body: JSON.stringify(patch),
  }));
  return data.store;
}

export async function brDeleteStore(id) {
  await authFetch(`/api/branch-radar/stores/${id}`, { method: "DELETE" });
}

export async function brListClasses() {
  const data = await jsonOrThrow(await authFetch(`/api/branch-radar/classes`));
  return data.classes || [];
}

export async function brSaveClass(cfg) {
  return jsonOrThrow(await authFetch(`/api/branch-radar/classes`, {
    method: "PUT", body: JSON.stringify(cfg),
  }));
}

// Site score for an arbitrary point (a store).
export async function brPointScore(payload) {
  return jsonOrThrow(await authFetch(`/api/pro/point-score`, {
    method: "POST", body: JSON.stringify(payload),
  }));
}

export async function brGetStoreDeepAnalysis(storeId) {
  const data = await jsonOrThrow(await authFetch(`/api/branch-radar/stores/${storeId}/deep-analysis`));
  return data.cache || {};
}

export async function brListStoreDAVersions(storeId) {
  const data = await jsonOrThrow(await authFetch(`/api/branch-radar/stores/${storeId}/deep-analysis/versions`));
  return data.versions || [];
}

export async function brGetStoreDAVersion(storeId, versionNo) {
  return jsonOrThrow(await authFetch(`/api/branch-radar/stores/${storeId}/deep-analysis/versions/${versionNo}`));
}

export async function brSaveStoreDeepAnalysis(storeId, body) {
  const data = await jsonOrThrow(await authFetch(`/api/branch-radar/stores/${storeId}/deep-analysis`, {
    method: "PUT", body: JSON.stringify(body),
  }));
  emitBalanceChanged();   // store deep analysis charged
  return data;
}

// Scan competitors around a candidate point without saving (add-store preview).
export async function brPreviewCompetitors(payload) {
  return jsonOrThrow(await authFetch(`/api/branch-radar/competitors/preview`, {
    method: "POST", body: JSON.stringify(payload),
  }));
}

export async function brListCompetitors(storeId) {
  const data = await jsonOrThrow(await authFetch(`/api/branch-radar/stores/${storeId}/competitors`));
  return data.competitors || [];
}

export async function brAddCompetitor(storeId, competitor) {
  const data = await jsonOrThrow(await authFetch(`/api/branch-radar/stores/${storeId}/competitors`, {
    method: "POST", body: JSON.stringify(competitor),
  }));
  return data.competitor;
}

export async function brDeleteCompetitor(storeId, compId) {
  await authFetch(`/api/branch-radar/stores/${storeId}/competitors/${compId}`, { method: "DELETE" });
}

export async function brRunMonitor(storeId, body) {
  return jsonOrThrow(await authFetch(`/api/branch-radar/stores/${storeId}/monitor`, {
    method: "POST", body: JSON.stringify(body),
  }));
}

export async function brListMonitorRuns(storeId) {
  const data = await jsonOrThrow(await authFetch(`/api/branch-radar/stores/${storeId}/monitor-runs`));
  return data.runs || [];
}

export async function brGetSubscription(storeId) {
  return jsonOrThrow(await authFetch(`/api/branch-radar/stores/${storeId}/subscription`));
}

export async function brSubscribe(storeId) {
  const data = await jsonOrThrow(await authFetch(`/api/branch-radar/stores/${storeId}/subscription`, { method: "POST" }));
  emitBalanceChanged();   // monthly subscription charged
  return data;
}

export async function brCancelSubscription(storeId) {
  return jsonOrThrow(await authFetch(`/api/branch-radar/stores/${storeId}/subscription`, { method: "DELETE" }));
}

export async function brSetSchedule(storeId, frequency) {
  return jsonOrThrow(await authFetch(`/api/branch-radar/stores/${storeId}/subscription/schedule`, {
    method: "PUT", body: JSON.stringify({ frequency }),
  }));
}
